import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GetSheetAgentRunByIdQuery,
} from "@/graphql/__generated__/graphql";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDownIcon, ChevronUpIcon, HelpCircle } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import SheetTaskComponent from "./SheetTaskComponent";
import { useTenderPermissionsData } from "@/hooks/cache";

/**
 * SheetTasksContainerComponent renders a list of tasks with input fields, grouped by document and sheet.
 * Each task is handled by its own SheetTaskComponent with individual save logic.
 * Tasks are organized in tabs by document, and within each tab, tasks are grouped by sheet in cards.
 *
 * @component
 * @param tenderId - The tender ID (for permissions)
 * @param tasks - The tasks array from the sheet agent run
 * @param documents - The documents array from the sheet agent run
 * @param onTaskIdsChange - Callback that receives array of task IDs that have answers
 * @example
 * <SheetTasksContainerComponent tenderId={47} tasks={tasks} documents={documents} onTaskIdsChange={(ids) => console.log(ids)} />
 */
interface SheetTasksContainerComponentProps {
  /** The tender ID (for permissions) */
  tenderId?: number | null;
  /** The tasks array from the sheet agent run */
  tasks: NonNullable<GetSheetAgentRunByIdQuery["getSheetAgentRunById"]>["tasks"];
  /** The documents array from the sheet agent run */
  documents: NonNullable<GetSheetAgentRunByIdQuery["getSheetAgentRunById"]>["documents"];
}

function SheetTasksContainerComponent({ tenderId, tasks, documents }: SheetTasksContainerComponentProps) {
  const { t } = useTranslation();
  const { canManageSheetAgent } = useTenderPermissionsData(tenderId || 0);

  // Track which collapsibles are open
  const [openCollapsibles, setOpenCollapsibles] = useState<Record<string, boolean>>({});

  // Create a map of documentId to document name
  const documentMap = useMemo(() => {
    const map = new Map<number, string>();
    documents.forEach((doc) => {
      map.set(doc.id, doc.fileName);
    });
    return map;
  }, [documents]);

  // Group tasks by documentId
  const tasksByDocument = useMemo(() => {
    const grouped = new Map<number, typeof tasks>();
    tasks.forEach((task) => {
      if (task.documentId) {
        if (!grouped.has(task.documentId)) {
          grouped.set(task.documentId, []);
        }
        grouped.get(task.documentId)?.push(task);
      }
    });
    return grouped;
  }, [tasks]);

  // Group tasks by sheet within a document
  const groupTasksBySheet = (documentTasks: typeof tasks) => {
    const grouped = new Map<string, typeof tasks>();
    documentTasks.forEach((task) => {
      const sheetName = task.sheet || t("edittender.sheetagent.taskscontainer.unknowsheet");
      if (!grouped.has(sheetName)) {
        grouped.set(sheetName, []);
      }
      grouped.get(sheetName)?.push(task);
    });
    return grouped;
  };

  // If no tasks provided, show empty state
  if (tasks.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        {t("edittender.sheetagent.taskscontainer.notasksavailable")}
      </div>
    );
  }

  // Get document IDs sorted for consistent tab order
  const documentIds = Array.from(tasksByDocument.keys()).sort((a, b) => {
    const nameA = documentMap.get(a) || "";
    const nameB = documentMap.get(b) || "";
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="h-full flex flex-col min-h-0">
      <Tabs defaultValue={String(documentIds[0])} className="h-full flex flex-col min-h-0">
        <div className="pt-2 overflow-x-auto">
          <TabsList className="flex-nowrap min-w-fit">
            {documentIds.map((docId) => {
              const docName = documentMap.get(docId) || `${t("edittender.sheetagent.taskscontainer.documentfallback")} ${docId}`;
              return (
                <TabsTrigger key={docId} value={String(docId)} className="shrink-0">
                  {docName}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
        {documentIds.map((docId) => {
          const documentTasks = tasksByDocument.get(docId) || [];
          const tasksBySheet = groupTasksBySheet(documentTasks);
          const sheetNames = Array.from(tasksBySheet.keys()).sort();

          return (
            <TabsContent
              key={docId}
              value={String(docId)}
              className="flex-1 min-h-0 overflow-y-auto pr-2 pb-[300px] space-y-4">
              {sheetNames.map((sheetName) => {
                const sheetTasks = tasksBySheet.get(sheetName) || [];
                const totalTasks = sheetTasks.length;
                const answeredTasks = sheetTasks.filter(
                  (task) =>
                    task.answer !== null &&
                    task.answer !== undefined &&
                    task.answer !== ""
                ).length;
                const collapsibleKey = `${docId}-${sheetName}`;
                const isOpen = openCollapsibles[collapsibleKey] ?? true;

                return (
                  <Collapsible
                    key={sheetName}
                    open={isOpen}
                    onOpenChange={(open) => {
                      setOpenCollapsibles((prev) => ({
                        ...prev,
                        [collapsibleKey]: open,
                      }));
                    }}
                    className="border rounded-lg"
                  >
                    <CollapsibleTrigger className="w-full sticky top-0 z-10 bg-background hover:cursor-pointer" asChild>
                      <div className="flex items-center gap-4 justify-between w-full px-3 pt-3 pb-2 hover:bg-accent transition-colors rounded-t-lg">
                        <div className="flex items-center gap-2 justify-between w-full">
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold">{sheetName}</h3>
                            <span className="text-sm text-muted-foreground">
                              ({answeredTasks}/{totalTasks})
                            </span>
                          </div>
                          <HoverCard openDelay={200}>
                            <HoverCardTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 p-0 hover:cursor-help text-muted-foreground hover:text-foreground"
                                type="button"
                                aria-label={t("edittender.sheetagent.task.type.help.aria")}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <HelpCircle className="h-4 w-4" />
                              </Button>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-80" align="start">
                              <div className="space-y-2">
                                <h4 className="text-sm font-semibold">
                                  {t("edittender.sheetagent.task.type.help.title")}
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  {t("edittender.sheetagent.task.type.help.description")}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {t("edittender.sheetagent.task.type.help.note")}
                                </p>
                              </div>
                            </HoverCardContent>
                          </HoverCard>
                        </div>
                        {isOpen ? (
                          <ChevronUpIcon className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-3 pb-3">
                      <div className="space-y-4 pt-2">
                        {sheetTasks.map((task, index) => (
                          <SheetTaskComponent
                            key={task.id}
                            task={task}
                            index={index}
                            canManageSheetAgent={canManageSheetAgent}
                          />
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}

export default SheetTasksContainerComponent;
